<template>
  <v-dialog persistent v-model="dialog" max-width="960px">
    <template slot="activator">
      <slot>
        <v-btn color="primary">
          <v-icon>file_upload</v-icon>
          Upload CSV
        </v-btn>
      </slot>
    </template>

    <v-card>
      <v-card-title class="primary">
        <span class="headline white--text">Gäste per CSV importieren</span>
      </v-card-title>

      <v-card-text>
        <!-- Anleitung (Deutsch) -->
        <v-alert type="info" :value="true" outline>
          <div class="body-1 font-weight-bold">Erwartetes CSV-Format</div>
          <div class="body-2">
            Kopfzeile muss exakt sein:
            <code>{{ expectedHeaders.join(";") }}</code>
            <br>
            Trennzeichen: Semikolon (;). Textwerte bei Semikolon/Leerzeichen in Anführungszeichen.
            <br>
            <strong>gender</strong>: <code>female</code>, <code>male</code> oder leer/anderer Wert (wird als leer gespeichert).<br>
            <strong>formal</strong>: <code>true</code> oder <code>false</code>.<br>
            <strong>email</strong>: gültige E-Mail. Pflichtfeld.<br>
            Leere zusätzliche Spalten sind nicht erlaubt. Reihenfolge der Spalten muss exakt passen.
            <br><br>
            <strong>Hinweis:</strong> Wenn ein Gast mit der gleichen E-Mail bereits existiert, wird dieser bestehende Gast dem Event hinzugefügt.
          </div>
          <div class="mt-2 body-2">
            Beispiel:
            <pre class="my-2 pa-2 grey lighten-4" style="white-space:pre-wrap;border-radius:4px;">
gender;title;forename;surname;email;formal
female;Dr.;Anna;Muster;"anna@example.org";true
male;;Max;Maier;max.maier@example.org;false
            </pre>
          </div>
        </v-alert>

        <!-- Datei Auswahl -->
        <v-layout wrap align-center>
          <v-flex xs12 sm8>
            <v-text-field
              :value="fileName"
              label="CSV-Datei"
              prepend-icon="attach_file"
              readonly
            />
          </v-flex>
          <v-flex xs12 sm4 class="text-sm-right">
            <input
              ref="file"
              type="file"
              accept=".csv,text/csv"
              style="display:none"
              @change="onFileChange"
            />
            <v-btn color="primary" @click="$refs.file.click()" :disabled="loading">
              Datei wählen
            </v-btn>
            <v-btn flat @click="clearFile" :disabled="loading">Zurücksetzen</v-btn>
          </v-flex>
        </v-layout>

        <!-- Validierungs-Ergebnisse -->
        <v-alert
          v-if="errors.length"
          type="error"
          :value="true"
          outline
          class="mt-3"
        >
          <div class="subheading">Fehler in der Datei</div>
          <ul class="body-2" style="margin-left:1rem;">
            <li v-for="(err, i) in errors" :key="i">{{ err }}</li>
          </ul>
        </v-alert>

        <!-- Warnungen -->
        <v-alert
          v-if="warnings.length"
          type="warning"
          :value="true"
          outline
          class="mt-3"
        >
          <div class="subheading">Warnungen</div>
          <ul class="body-2" style="margin-left:1rem;">
            <li v-for="(warn, i) in warnings" :key="i">{{ warn }}</li>
          </ul>
        </v-alert>

        <!-- Preview Tabelle -->
        <v-card v-if="rows.length" class="mt-3" flat>
          <v-card-title class="px-0">Vorschau ({{ rows.length }} Zeilen)</v-card-title>
          <v-data-table
            :headers="previewHeaders"
            :items="rows"
            :rows-per-page-items="[5,10,25,{'text':'Alle','value':-1}]"
            class="elevation-1"
          >
            <template slot="items" slot-scope="props">
              <td>
                <v-chip
                  small
                  :color="props.item.statusColor"
                  text-color="white"
                >
                  {{ props.item.statusText }}
                </v-chip>
              </td>
              <td>{{ props.item.gender }}</td>
              <td>{{ props.item.title }}</td>
              <td>{{ props.item.forename }}</td>
              <td>{{ props.item.surname }}</td>
              <td>{{ props.item.email }}</td>
              <td>{{ String(props.item.formal) }}</td>
            </template>
          </v-data-table>
        </v-card>

        <small class="grey--text text--darken-1 d-block mt-3">* Bei Fehlern werden nur fehlerfreie Gäste verarbeitet. Existierende Gäste werden dem Event hinzugefügt.</small>
      </v-card-text>

      <v-card-actions>
        <v-spacer></v-spacer>
        <v-btn flat color="primary" @click.native="cancel()" :disabled="loading">Abbrechen</v-btn>
        <v-btn
          color="primary"
          @click.native="save()"
          :disabled="!canImport"
          :loading="loading"
        >
          Import starten
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import { mapActions } from "vuex";

export default {
  name: "DlgGuestImportCSV",
  props: {
    event: {
      type: Object,
      default: null,
    },
    existingGuests: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      dialog: false,
      fileName: "",
      rawText: "",
      loading: false,
      rows: [],
      errors: [],
      warnings: [],
      expectedHeaders: ["gender", "title", "forename", "surname", "email", "formal"],
      previewHeaders: [
        { text: "Status", value: "status" },
        { text: "gender", value: "gender" },
        { text: "title", value: "title" },
        { text: "forename", value: "forename" },
        { text: "surname", value: "surname" },
        { text: "email", value: "email" },
        { text: "formal", value: "formal" },
      ],
      emailRegex: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
      allGuestsCache: [], // Cache all guests when dialog opens
    };
  },
  computed: {
    canImport() {
      return this.rows.length > 0 && this.errors.length === 0 && !this.loading;
    },
  },
  watch: {
    async dialog(newVal) {
      if (newVal) {
        // Fetch all guests when dialog opens
        await this.readGuests();
        this.allGuestsCache = this.$store.getters['admin/guests/all'];
      }
    },
  },
  methods: {
    ...mapActions({
      createGuest: "admin/guests/create",
      readGuests: "admin/guests/read",
      addGuestsToEvent: "admin/events/addGuests",
      readEventGuests: "admin/events/readGuests",
    }),

    cancel() {
      this.dialog = false;
      this.clearFile();
    },

    clearFile() {
      this.fileName = "";
      this.rawText = "";
      this.rows = [];
      this.errors = [];
      this.warnings = [];
      if (this.$refs.file) this.$refs.file.value = "";
    },

    onFileChange(e) {
      this.errors = [];
      this.warnings = [];
      this.rows = [];
      const file = e.target.files && e.target.files[0];
      if (!file) return;
      this.fileName = file.name;

      const reader = new FileReader();
      reader.onload = () => {
        this.rawText = String(reader.result || "");
        this.parseAndValidate(this.rawText);
      };
      reader.onerror = () => {
        this.errors.push("Datei konnte nicht gelesen werden.");
      };
      reader.readAsText(file, "utf-8");
    },

    // Minimal robuster CSV-Parser mit Quote-Support (RFC4180-nah, kein CRLF-Zwang)
    parseCSVStrict(text) {
      const rows = [];
      let i = 0, field = "", inQuotes = false, row = [];
      const pushField = () => { row.push(field); field = ""; };
      const pushRow = () => { rows.push(row); row = []; };

      while (i < text.length) {
        const ch = text[i];

        if (inQuotes) {
          if (ch === '"') {
            const next = text[i + 1];
            if (next === '"') { field += '"'; i += 2; continue; } // escaped quote
            inQuotes = false; i++; continue;
          } else {
            field += ch; i++; continue;
          }
        } else {
          if (ch === '"') { inQuotes = true; i++; continue; }
          if (ch === ";") { pushField(); i++; continue; }
          if (ch === "\n") { pushField(); pushRow(); i++; continue; }
          if (ch === "\r") { // handle \r\n or \r
            pushField();
            if (text[i + 1] === "\n") i++;
            pushRow(); i++; continue;
          }
          field += ch; i++; continue;
        }
      }
      // last field/row
      pushField();
      // avoid trailing empty row if file ends with newline
      if (row.length > 1 || (row.length === 1 && row[0] !== "")) {
        pushRow();
      }
      return rows;
    },

    parseAndValidate(text) {
      const grid = this.parseCSVStrict(text);
      if (!grid.length) {
        this.errors.push("Die Datei ist leer.");
        return;
      }

      // Header prüfen
      const header = grid[0].map(h => h.trim());
      if (header.length !== this.expectedHeaders.length) {
        this.errors.push(
          `Kopfzeile hat ${header.length} Spalten, erwartet sind ${this.expectedHeaders.length}.`
        );
        return;
      }
      for (let i = 0; i < this.expectedHeaders.length; i++) {
        if (header[i] !== this.expectedHeaders[i]) {
          this.errors.push(
            `Kopfzeile Spalte ${i + 1} ist "${header[i]}", erwartet "${this.expectedHeaders[i]}".`
          );
        }
      }
      if (this.errors.length) return;

      // Datenzeilen einlesen
      const dataRows = grid.slice(1);
      if (!dataRows.length) {
        this.errors.push("Keine Datenzeilen gefunden.");
        return;
      }

      const seenEmails = new Map(); // Track email -> line number for duplicates
      const duplicateWarnings = [];
      const out = [];
      const emailMap = new Map(); // Track email -> data object (will keep last occurrence)

      dataRows.forEach((cols, idx) => {
        const line = idx + 2; // 1-based + header
        if (cols.length !== this.expectedHeaders.length) {
          this.errors.push(`Zeile ${line}: hat ${cols.length} Spalten, erwartet ${this.expectedHeaders.length}.`);
          return;
        }

        const obj = {
          gender: cols[0].trim(),
          title: cols[1].trim(),
          forename: cols[2].trim(),
          surname: cols[3].trim(),
          email: cols[4].trim().toLowerCase(),
          formal: cols[5].trim(),
        };

        // Feldprüfungen
        if (!obj.email) this.errors.push(`Zeile ${line}: email fehlt.`);
        if (obj.email && !this.emailRegex.test(obj.email)) this.errors.push(`Zeile ${line}: email ungültig: "${obj.email}"`);
        // Gender validation - allow empty/invalid values, will be set to null
        if (obj.formal !== "true" && obj.formal !== "false") {
          this.errors.push(`Zeile ${line}: formal muss "true" oder "false" sein.`);
        }

        // Track duplicates but continue processing
        const emailKey = obj.email.toLowerCase();
        if (seenEmails.has(emailKey)) {
          const firstLine = seenEmails.get(emailKey);
          duplicateWarnings.push(`Zeile ${line}: email "${obj.email}" bereits in Zeile ${firstLine} vorhanden. Verwende Daten aus Zeile ${line}.`);
        } else {
          seenEmails.set(emailKey, line);
        }

        // Store normalized data, will overwrite previous occurrences of same email
        emailMap.set(emailKey, {
          gender: (obj.gender === "female" || obj.gender === "male") ? obj.gender : null,
          title: obj.title || null,
          forename: obj.forename || null,
          surname: obj.surname || null,
          email: obj.email,
          formal: obj.formal === "true",
        });
      });

      // Show duplicate warnings (non-blocking)
      if (duplicateWarnings.length) {
        this.warnings = duplicateWarnings;
      }

      // Convert map values to array (will have unique emails with last occurrence)
      out.push(...emailMap.values());

      if (this.errors.length) {
        // Block if there are real errors
        this.rows = [];
        return;
      }

      // Add status information to each row and filter out guests already in event
      const existingGuestEmails = new Set(
        this.existingGuests.map(g => g.email ? g.email.toLowerCase() : '')
      );

      this.rows = out
        .map(guest => {
          const emailKey = guest.email.toLowerCase();
          const existsInDB = this.allGuestsCache.some(g => g.email && g.email.toLowerCase() === emailKey);
          const existsInEvent = existingGuestEmails.has(emailKey);

          let statusText;
          let statusColor;

          if (existsInEvent) {
            statusText = 'Im Event';
            statusColor = 'grey';
          } else if (existsInDB) {
            statusText = 'In DB';
            statusColor = 'orange';
          } else {
            statusText = 'Neu';
            statusColor = 'green';
          }

          return {
            ...guest,
            statusText,
            statusColor,
            existsInEvent, // Add flag for filtering
          };
        })
        .filter(guest => !guest.existsInEvent); // Only show guests not already in event
    },

    async save() {
      if (!this.canImport) return;

      this.loading = true;
      const created = [];
      const existing = [];
      const failed = [];
      const alreadyInEvent = [];
      const guestIds = [];

      try {
        // Create a set of emails for guests already in THIS event
        const existingGuestEmails = new Set(
          this.existingGuests.map(g => g.email ? g.email.toLowerCase() : '')
        );

        // Load all guests in the system first to avoid multiple API calls
        await this.readGuests();
        const allGuests = this.$store.getters['admin/guests/all'];

        // Process each guest from CSV
        for (let i = 0; i < this.rows.length; i++) {
          const payload = this.rows[i];
          const emailKey = payload.email.toLowerCase();

          // Check if guest already exists in the system
          const existingGuest = allGuests.find(g => g.email && g.email.toLowerCase() === emailKey);

          if (existingGuest && existingGuest.id) {
            // Guest exists in system
            existing.push(existingGuest);

            // Check if guest is already in THIS event
            if (existingGuestEmails.has(emailKey)) {
              alreadyInEvent.push(existingGuest);
              // Don't add to guestIds since already in event
            } else {
              // Guest exists but not in this event - add to event
              guestIds.push(existingGuest.id);
            }
          } else {
            // Guest doesn't exist in system - create new guest
            try {
              const guest = await this.createGuest(payload);
              if (guest && guest.id) {
                created.push(guest);
                guestIds.push(guest.id);
              } else {
                failed.push({ index: i, email: payload.email, error: "unbekannter Fehler" });
              }
            } catch (e) {
              const errorMsg = e && e.message ? e.message : String(e);
              failed.push({ index: i, email: payload.email, error: errorMsg });
            }
          }
        }

        // If we have an event, add guests that are not already in the event
        if (this.event && this.event.id && guestIds.length > 0) {
          try {
            await this.addGuestsToEvent({ id: this.event.id, guests: guestIds });
            await this.readEventGuests({ id: this.event.id });
          } catch (e) {
            const errorMsg = e && e.message ? e.message : String(e);
            this.errors.push(`Fehler beim Hinzufügen der Gäste zum Event: ${errorMsg}`);
          }
        }

        await this.readGuests();
      } finally {
        this.loading = false;
      }

      if (failed.length) {
        // Show API errors
        this.errors = failed.map(f =>
          `API-Fehler bei Zeile ${f.index + 2} (${f.email}): ${f.error}`
        );
      } else {
        this.errors = [];
      }

      // Add info about skipped guests
      if (alreadyInEvent.length > 0) {
        const skippedEmails = alreadyInEvent.map(g => g.email).join(', ');
        this.warnings = [...(this.warnings || []), `${alreadyInEvent.length} Gast(e) bereits im Event vorhanden und übersprungen: ${skippedEmails}`];
      }

      const totalProcessed = created.length + existing.length;
      const totalAdded = guestIds.length;
      if (totalProcessed > 0 && !failed.length) {
        this.$emit("imported", {
          count: totalProcessed,
          created: created,
          existing: existing,
          alreadyInEvent: alreadyInEvent,
          added: totalAdded,
          items: [...created, ...existing]
        });
        this.clearFile();
        this.dialog = false;
      } else if (totalProcessed > 0 && failed.length > 0) {
        // Partial success - show what was processed
        this.$emit("imported", {
          count: totalProcessed,
          created: created,
          existing: existing,
          alreadyInEvent: alreadyInEvent,
          added: totalAdded,
          items: [...created, ...existing],
          partial: true,
          failedCount: failed.length
        });
      }
    },
  },
};
</script>

<style scoped>
code { padding: 0 4px; background: rgba(0,0,0,0.05); border-radius: 3px; }
pre { overflow: auto; }
</style>
